import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, User, ChevronDown,  Mail } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import LanguageToggle from '../../LanguageToggle';
import { getAvatarUrl } from '../../../utils/avatarUtils';

export default function Topnav({ toggleSidebar }) {
    const [ShowSearchbar, SetShowSearchbar] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showNotifDropdown, setShowNotifDropdown] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [userData, setUserData] = useState(null);
    
    const isExpertPath = location.pathname.includes("/expert-dashboard");
    const isRetailPath = location.pathname.includes("/retailer-dashboard");
   
    // Fetch user data from localStorage and refresh periodically
    useEffect(() => {
        const loadUserData = () => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            setUserData(user);
        };
        
        loadUserData();
        
        // Refresh user data every 30 seconds to get updated avatar
        const interval = setInterval(loadUserData, 30000);
        
        return () => clearInterval(interval);
    }, []);
    
    // refs for the dropdown containers
    const userDropdownRef = useRef(null);
    const notifDropdownRef = useRef(null);
    const userButtonRef = useRef(null);
    const notifButtonRef = useRef(null);
    
    //  user role based on path
    const getUserRole = () => {
        if (isExpertPath) return "Agric Expert";
        if (isRetailPath) return "Retailer";
        return "Farmer";
    };

    // Get user name
    const getUserName = () => {
        if (userData && userData.first_name && userData.last_name) {
            return `${userData.first_name} ${userData.last_name}`;
        }
        return "User";
    };

    // Get user email
    const getUserEmail = () => {
        return userData?.email || "user@example.com";
    };

    // Get user image
    const getUserImage = () => {
        const avatar = userData?.avatar || userData?.avatar_url;
        const fullName = userData?.full_name || `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim();
        return getAvatarUrl(
            avatar,
            fullName,
            userData?.first_name || '',
            userData?.last_name || '',
            50
        );
    };
    
    //  message sender based on user role
    const getMessageSender = () => {
        if (isExpertPath) return "a Farmer";
        if (isRetailPath) return "a Supplier";
        return "Agric Expert";
    };
    
    // Sample notification data
    const [notifications, setNotifications] = useState([
      /*   {
            id: 1,
            title: "New message",
            message: `You have a new message from ${getMessageSender()}`,
            time: "2 mins ago",
            read: false
        } */
    ]);
    
    // Effect to handle clicks outside dropdowns
    useEffect(() => {
        function handleClickOutside(event) {
            if (showDropdown && 
                userDropdownRef.current && 
                !userDropdownRef.current.contains(event.target) &&
                !userButtonRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
            
            if (showNotifDropdown && 
                notifDropdownRef.current && 
                !notifDropdownRef.current.contains(event.target) &&
                !notifButtonRef.current.contains(event.target)) {
                setShowNotifDropdown(false);
            }
        }

        if (showDropdown || showNotifDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, showNotifDropdown]);
    
    function ToggleSearchbar() {
        SetShowSearchbar(!ShowSearchbar);
    }
   
    
    function toggleDropdown() {
        setShowDropdown(!showDropdown);
        if(showNotifDropdown) setShowNotifDropdown(false);
    }
    
    function toggleNotifDropdown() {
        setShowNotifDropdown(!showNotifDropdown);
        if(showDropdown) setShowDropdown(false);
        
        if(!showNotifDropdown) {
            setNotifications(notifications.map(notif => ({
                ...notif,
                read: true
            })));
        }
    }
    
    function markAsRead(id) {
        setNotifications(notifications.map(notif => 
            notif.id === id ? {...notif, read: true} : notif
        ));
    }

    //  path for links based on user type
    const getBasePath = () => {
        if (isExpertPath) return "/expert-dashboard";
        if (isRetailPath) return "/retailer-dashboard";
        return "/farmer-dashboard";
    };


    return (
        <div className='topnav'>
            <div className="dashboard-search">
                <i className='fas fa-bars unseen' onClick={toggleSidebar}></i>
            </div>
            <div className="last-part">
                <div className="notif-btn">
                    <button 
                        ref={notifButtonRef}
                        className="notif-icon" 
                        onClick={toggleNotifDropdown}
                    >
                        <Bell />
                        {notifications.some(n => !n.read) && (
                            <span className="notif-badge"></span>
                        )}
                    </button>
                    
                    {showNotifDropdown && (
                        <div className="notif-dropdown" ref={notifDropdownRef}>
                            <div className="notif-header">
                                <h4>Notifications</h4>
                                <span>{notifications.filter(n => !n.read).length} new</span>
                            </div>
                            <div className="notif-list">
                                {notifications.length > 0 ? (
                                    notifications.map(notification => (
                                        <div 
                                            key={notification.id} 
                                            className={`notif-item ${!notification.read ? 'unread' : ''}`}
                                            onClick={() => markAsRead(notification.id)}
                                        >
                                            <div className="notif-content">
                                                <h5>{notification.title}</h5>
                                                <p>{notification.message}</p>
                                                <small>{notification.time}</small>
                                            </div>
                                            {!notification.read && (
                                                <div className="unread-dot"></div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-notifs">
                                        No new notifications
                                    </div>
                                )}
                            </div>
                            <div className="notif-footer" onClick={toggleNotifDropdown}>
                                <Link to={`${getBasePath()}/notifications`}>View all</Link>
                            </div>
                        </div>
                    )}
                    
                    <Link 
                        to={`${getBasePath()}/messages`} 
                        className="message-icon"
                    >
                        <span className="notif-badge"></span>
                        <Mail />
                    </Link>
                     
                    
                </div>
                
                <LanguageToggle />

                <div
                    className="user-section"
                    onClick={toggleDropdown}
                    ref={userButtonRef}
                >
                    <div className="user-img">
                        <img src={getUserImage()} alt={getUserName()} />
                        <div className="user-name">
                            <p>{getUserName()}</p>
                            <h4 className='role'>{getUserRole()}</h4>
                        </div>
                    </div>
                   
                    <ChevronDown size={16} className={`dropdown-arrow ${showDropdown ? 'active' : ''}`} />
                    
                    {showDropdown && (
                        <div className="user-drop-down" ref={userDropdownRef}>
                           <div className="user-drop-down-header">
                                <div className="user-info">
                                    <img src={getUserImage()} alt={getUserName()} className="user-avatar" />
                                    <div className="user-details">
                                        <h4>{getUserName()}</h4>
                                        <p>{getUserEmail()}</p>
                                    </div>
                                </div>
                                <div className="user-status">
                                    <div className="status-dot"></div>
                                    <span>Online</span>
                                </div>
                            </div>
                            
                            <div className="user-drop-down-body">
                                <Link 
                                    to={`${getBasePath()}/settings/account`} 
                                    className='user-drop-down-item'
                                >
                                    <User />
                                    My Profile
                                </Link>
                                
                                <button 
                                    className='user-drop-down-item logout' 
                                    onClick={async () => {
                                        await logout();
                                        navigate('/');
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        width: '100%',
                                        color: 'inherit',
                                        fontSize: 'inherit',
                                        cursor: 'pointer',
                                        padding: '12px 16px',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <LogOut />
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}